// Mirror of backend/internal/protocol (Go). Keep these in sync with the server.

export type GameStatus = 'lobby' | 'active' | 'finished';
export type QuestionState = 'idle' | 'open' | 'closed';
export type QuestionMode = 'classic' | 'shout';
export type BuzzResult = 'pending' | 'correct' | 'incorrect';
/** Fixed at creation: classic buzzer quiz, or a «Норм чи Стрьом» vote round. */
export type GameKind = 'quiz' | 'normcringe';
export type VoteChoice = 'norm' | 'cringe';

// Client -> Server actions
export const Action = {
  PlayerJoin: 'player.join',
  PlayerIKnow: 'player.iKnow',
  PlayerVote: 'player.vote', // normcringe games only
  AdminStart: 'admin.startGame',
  AdminOpenQ: 'admin.openQuestion',
  AdminResetQ: 'admin.resetQuestion',
  AdminCallOn: 'admin.callOn',
  AdminMark: 'admin.mark',
  AdminUnmark: 'admin.unmark',
  AdminSetMode: 'admin.setQuestionMode',
  AdminAward: 'admin.awardShout',
  AdminNextQ: 'admin.nextQuestion',
  AdminPrevQ: 'admin.prevQuestion',
  AdminNextTop: 'admin.nextTopic',
  AdminPrevTop: 'admin.prevTopic',
  AdminEndGame: 'admin.endGame',
  Sync: 'sync',
} as const;

// Server -> Client events
export const Event = {
  GameState: 'game.state',
  Welcome: 'player.welcome',
  PlayerJoined: 'player.joined',
  QuestionOpen: 'question.opened',
  QueueUpdate: 'queue.updated',
  QuestionShut: 'question.closed',
  CalledOn: 'player.calledOn',
  AnswerResult: 'answer.result',
  ScoreUpdate: 'score.updated',
  GameEnded: 'game.ended',
  VoteUpdate: 'vote.updated', // admin-only live tally while a statement is open
  VoteReveal: 'vote.revealed', // everyone: statement closed, here is the split
  Error: 'error',
} as const;

export interface ClientMessage {
  action: string;
  data?: unknown;
}

export interface ServerMessage {
  type: string;
  data?: unknown;
}

export type MediaType = 'image' | 'audio';

export interface Attachment {
  url: string;
  type: MediaType | '';
}

/** Per-answer media of a vote statement (two pictures / two clips to choose between). */
export interface SideMedia {
  norm: Attachment;
  cringe: Attachment;
}

export interface PublicQuestion {
  id: string;
  text: string;
  points: number;
  order: number;
  /** attachment: "/media/<key>" or an https link; players never get an audio URL */
  mediaUrl?: string;
  mediaType?: MediaType;
  /** normcringe: per-answer media; audio URLs are stripped for players */
  sides?: SideMedia;
}

export interface QueueEntry {
  playerId: string;
  nickname: string;
  seq: number;
  result: BuzzResult;
  /** ms after the tier reference (first correct buzz, else first buzz); negative = earlier */
  deltaMs: number;
  /** what marking this buzz correct would award right now (0 = too late) */
  pointsOnCorrect: number;
  /** what a correct mark actually granted */
  pointsAwarded: number;
}

export interface ScoreEntry {
  playerId: string;
  nickname: string;
  score: number;
}

/** Custom wording for the two vote buttons; absent → client defaults (Норм / Стрьом). */
export interface VoteLabels {
  norm: string;
  cringe: string;
}

export interface VoteTally {
  norm: number;
  cringe: number;
  total: number;
  /** the statement's right answer, present only once it may be shown (admins; players after close) */
  correct?: VoteChoice;
  /** who scored on a closed statement with a correct answer, fastest first */
  awards?: VoteAward[];
}

export interface VoteAward {
  playerId: string;
  nickname: string;
  points: number;
  elapsedMs: number;
}

export interface VoteSummaryEntry {
  questionId: string;
  topicTitle: string;
  text: string;
  tally: VoteTally;
}

export interface GameStateData {
  gameId: string;
  code: string;
  status: GameStatus;
  kind: GameKind;
  /** effective buzzer/vote window length in seconds */
  windowSeconds: number;
  voteLabels?: VoteLabels;
  donateUrl?: string;
  questionState: QuestionState;
  questionMode: QuestionMode;
  topicTitle?: string;
  question?: PublicQuestion;
  endsAt?: string;
  queue: QueueEntry[];
  scores: ScoreEntry[];
  answerNote?: string;
  /** normcringe: current statement's tally — admins always, players once closed */
  votes?: VoteTally;
  /** normcringe: every statement's final split, once the game has finished */
  voteSummary?: VoteSummaryEntry[];
}

export interface QuestionOpenData {
  question: PublicQuestion;
  topicTitle: string;
  endsAt: string;
  answerNote?: string;
}

export interface WelcomeData {
  playerId: string;
  nickname: string;
  gameId: string;
}

export interface CalledOnData {
  playerId: string;
  nickname: string;
}

export interface AnswerResultData {
  playerId: string;
  nickname: string;
  correct: boolean;
  points: number;
}

export interface ErrorData {
  message: string;
}

/**
 * Points each of `n` shouting-match winners gets for a question worth `price`:
 * the pot split equally and rounded up, but never below price/10 (also rounded up).
 * Mirrors game.ShoutPoints in the Go backend — keep the two in sync.
 */
export function shoutPoints(price: number, n: number): number {
  if (n <= 0 || price <= 0) return 0;
  const ceilDiv = (a: number, b: number) => Math.ceil(a / b);
  return Math.max(ceilDiv(price, n), ceilDiv(price, 10));
}
